import { createActions } from 'reduxsauce'

/* ------------- Types and Action Creators ------------- */
const { Types, Creators } = createActions({
  // Search Sound Effects
  soundEffectsSearchRequest: ['query', 'options'],
  soundEffectsSearchSuccess: ['results'],
  soundEffectsSearchFailure: ['error'],

  // Get Categories
  soundEffectsCategoriesRequest: null,
  soundEffectsCategoriesSuccess: ['categories'],
  soundEffectsCategoriesFailure: ['error'],

  // Get Video Sound Effect Clips
  soundEffectClipsGetRequest: ['videoId'],
  soundEffectClipsGetSuccess: ['clips'],
  soundEffectClipsGetFailure: ['error'],

  // Add Sound Effect Clip
  soundEffectClipAddRequest: ['videoId', 'clipData'],
  soundEffectClipAddSuccess: ['clip'],
  soundEffectClipAddFailure: ['error'],

  // Update Sound Effect Clip
  soundEffectClipUpdateRequest: ['videoId', 'clipId', 'updates'],
  soundEffectClipUpdateSuccess: ['clip'],
  soundEffectClipUpdateFailure: ['error'],

  // Delete Sound Effect Clip
  soundEffectClipDeleteRequest: ['videoId', 'clipId'],
  soundEffectClipDeleteSuccess: [],
  soundEffectClipDeleteFailure: ['error'],

  // Set current clips (local state)
  setCurrentClips: ['clips'],
  addClip: ['clip'],
  updateClip: ['clipId', 'updates'],
  removeClip: ['clipId'],
  clearClips: [],

  // Reset
  soundEffectsReset: [],
})

export const SoundEffectTypes = Types
export default Creators

/* ------------- Initial State ------------- */
const INITIAL_STATE = {
  // Search
  searchResults: [],
  searchFetching: false,
  searchError: null,

  // Categories
  categories: [],
  categoriesFetching: false,
  categoriesError: null,

  // Clips
  clips: [],
  clipsFetching: false,
  clipsError: null,

  // Current clips (local working state)
  currentClips: [],

  // Add/Update/Delete
  mutationFetching: false,
  mutationError: null,
}

/* ------------- Stable Default Values ------------- */
const EMPTY_ARRAY = []

/* ------------- Selectors ------------- */
export const SoundEffectSelectors = {
  // Search
  getSearchResults: (state) => state.soundEffect.searchResults || EMPTY_ARRAY,
  isSearching: (state) => state.soundEffect.searchFetching,
  getSearchError: (state) => state.soundEffect.searchError,

  // Categories
  getCategories: (state) => state.soundEffect.categories || EMPTY_ARRAY,
  isCategoriesFetching: (state) => state.soundEffect.categoriesFetching,
  getCategoriesError: (state) => state.soundEffect.categoriesError,

  // Clips
  getClips: (state) => state.soundEffect.clips || EMPTY_ARRAY,
  isClipsFetching: (state) => state.soundEffect.clipsFetching,
  getClipsError: (state) => state.soundEffect.clipsError,

  // Current clips
  getCurrentClips: (state) => state.soundEffect.currentClips || EMPTY_ARRAY,

  // Mutations
  isMutating: (state) => state.soundEffect.mutationFetching,
  getMutationError: (state) => state.soundEffect.mutationError,
}

/* ------------- Reducer ------------- */
export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // Search
    case Types.SOUND_EFFECTS_SEARCH_REQUEST:
      return { ...state, searchFetching: true, searchError: null }
    case Types.SOUND_EFFECTS_SEARCH_SUCCESS:
      return { ...state, searchResults: action.results, searchFetching: false, searchError: null }
    case Types.SOUND_EFFECTS_SEARCH_FAILURE:
      return { ...state, searchFetching: false, searchError: action.error }

    // Categories
    case Types.SOUND_EFFECTS_CATEGORIES_REQUEST:
      return { ...state, categoriesFetching: true, categoriesError: null }
    case Types.SOUND_EFFECTS_CATEGORIES_SUCCESS:
      return { ...state, categories: action.categories, categoriesFetching: false, categoriesError: null }
    case Types.SOUND_EFFECTS_CATEGORIES_FAILURE:
      return { ...state, categoriesFetching: false, categoriesError: action.error }

    // Get Clips
    case Types.SOUND_EFFECT_CLIPS_GET_REQUEST:
      return { ...state, clipsFetching: true, clipsError: null }
    case Types.SOUND_EFFECT_CLIPS_GET_SUCCESS:
      return { ...state, clips: action.clips, clipsFetching: false, clipsError: null }
    case Types.SOUND_EFFECT_CLIPS_GET_FAILURE:
      return { ...state, clipsFetching: false, clipsError: action.error }

    // Add Clip
    case Types.SOUND_EFFECT_CLIP_ADD_REQUEST:
      return { ...state, mutationFetching: true, mutationError: null }
    case Types.SOUND_EFFECT_CLIP_ADD_SUCCESS:
      return { ...state, mutationFetching: false, mutationError: null, clips: [...state.clips, action.clip] }
    case Types.SOUND_EFFECT_CLIP_ADD_FAILURE:
      return { ...state, mutationFetching: false, mutationError: action.error }

    // Update Clip
    case Types.SOUND_EFFECT_CLIP_UPDATE_REQUEST:
      return { ...state, mutationFetching: true, mutationError: null }
    case Types.SOUND_EFFECT_CLIP_UPDATE_SUCCESS:
      return { ...state, mutationFetching: false, mutationError: null, clips: state.clips.map((c) => (c.id === action.clip.id ? action.clip : c)) }
    case Types.SOUND_EFFECT_CLIP_UPDATE_FAILURE:
      return { ...state, mutationFetching: false, mutationError: action.error }

    // Delete Clip
    case Types.SOUND_EFFECT_CLIP_DELETE_REQUEST:
      return { ...state, mutationFetching: true, mutationError: null }
    case Types.SOUND_EFFECT_CLIP_DELETE_SUCCESS:
      return { ...state, mutationFetching: false, mutationError: null }
    case Types.SOUND_EFFECT_CLIP_DELETE_FAILURE:
      return { ...state, mutationFetching: false, mutationError: action.error }

    // Current Clips (local state)
    case Types.SET_CURRENT_CLIPS:
      return { ...state, currentClips: action.clips }
    case Types.ADD_CLIP:
      return { ...state, currentClips: [...state.currentClips, action.clip] }
    case Types.UPDATE_CLIP:
      return { ...state, currentClips: state.currentClips.map((c) => c.id === action.clipId ? { ...c, ...action.updates } : c) }
    case Types.REMOVE_CLIP:
      return { ...state, currentClips: state.currentClips.filter((c) => c.id !== action.clipId) }
    case Types.CLEAR_CLIPS:
      return { ...state, currentClips: [] }

    // Reset
    case Types.SOUND_EFFECTS_RESET:
      return { ...INITIAL_STATE }

    default:
      return state
  }
}
